
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/apifetch";
import { useAuth } from "../App";
import "./AdminSettings.css";

//const API = import.meta.env.VITE_API_URL;
const API = "";

export default function AdminSettings() {
  const navigate = useNavigate();
  const { logout, reloadIdleTimeout } = useAuth();

  /* ================= AUTH HEADER ================= */
  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  /* ================= THEME ================= */
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light"
  );

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
  }, [theme]);

  /* ================= SETTINGS STATE ================= */
  const [idleLogoutMinutes, setIdleLogoutMinutes] = useState("");
  const [logRetentionDays, setLogRetentionDays] = useState("");
  const [currentStatus, setCurrentStatus] = useState("LIVE NOW");

  const [forceUid, setForceUid] = useState("");
  const [forceMsg, setForceMsg] = useState("");
  const [forceError, setForceError] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ================= LOAD SETTINGS ================= */
  useEffect(() => {
    let mounted = true;

    const loadSettings = async () => {
      try {
        const res = await apiFetch(
          `${API}/settings`,
          { headers: authHeader() },
          logout,
          navigate
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.message || "Failed to load settings");
        }

        if (!mounted) return;

        setIdleLogoutMinutes(data.idle_logout_minutes ?? "");
        setLogRetentionDays(data.activity_log_retention_days ?? "");
        setCurrentStatus(data.current_status ?? "LIVE NOW");
      } catch (err) {
        if (mounted) {
          setError(err.message || "Failed to load settings");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSettings();
    return () => {
      mounted = false;
    };
  }, [logout, navigate]);

  /* ================= SAVE SETTINGS ================= */
  const handleSave = async () => {
    if (currentStatus === "UNDER MAINTENANCE") {
      const ok = window.confirm(
        "This will log out ALL users including you. Continue?"
      );
      if (!ok) return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {


      const res = await apiFetch(
        `${API}/settings`,
        {
          method: "PUT",
          headers: authHeader(),
          body: JSON.stringify({
            idle_logout_minutes: Number(idleLogoutMinutes),
            activity_log_retention_days: Number(logRetentionDays),
            current_status: currentStatus,
          }),
        },
        logout,
        navigate
      );

      const data = await res.json();

      if (!res.ok || data?.success !== true) {
        throw new Error(data?.message || "Settings not persisted");
      }

      setSuccess("Settings saved successfully");
      
      /* reload timeout in this tab */
      await reloadIdleTimeout();

      /* reset idle timer immediately */
      window.dispatchEvent(new Event("mousemove"));

    } catch (err) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  /* ================= FORCE LOGOUT USER ================= */
  const handleForceLogoutUser = async () => {
    setForceMsg("");
    setForceError("");

    if (!forceUid.trim()) {
      setForceError("Please enter a User UID");
      return;
    }

    const ok = window.confirm(
      `Force logout user "${forceUid}" from all sessions?`
    );
    if (!ok) return;

    try {
      const res = await apiFetch(
        `${API}/admin/users/${forceUid}/force-logout`,
        { method: "POST", headers: authHeader() },
        logout,
        navigate
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Force logout failed");
      }

      setForceMsg(data.message || "User logged out successfully");
      setForceUid("");
    } catch (err) {
      setForceError(err.message || "Failed to force logout user");
    }
  };

  /* ================= FORCE LOGOUT ALL ================= */
  const handleForceLogoutAll = async () => {
    setForceMsg("");
    setForceError("");

    const ok = window.confirm(
      "⚠️ This will log out ALL users (including you). Continue?"
    );
    if (!ok) return;

    try {
      const res = await apiFetch(
        `${API}/admin/users/force-logout-all`,
        { method: "POST", headers: authHeader() },
        logout,
        navigate
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data?.message || "Force logout all failed");
      }

      logout();
    } catch (err) {
      setForceError(err.message || "Failed to force logout all users");
    }
  };

  /* ================= RENDER ================= */
  if (loading) {
    return <div className="admin-loading">Loading settings…</div>;
  }

  return (
    <div className={`admin-settings ${theme}`}>
      <div className="admin-settings-header">
        <h2>Admin Settings</h2>
      </div>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      <div className="settings-grid">
        <div className="settings-card danger">
          <h3 className="card-title">Application Settings</h3>

          <div className="form-group">
            <label>Idle Logout (minutes)</label>
            <input
              type="number"
              value={idleLogoutMinutes}
              onChange={(e) => setIdleLogoutMinutes(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Log Retention (days)</label>
            <input
              type="number"
              value={logRetentionDays}
              onChange={(e) => setLogRetentionDays(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Application Status</label>
            <select
              value={currentStatus}
              onChange={(e) => setCurrentStatus(e.target.value)}
            >
              <option value="LIVE NOW">LIVE NOW</option>
              <option value="UNDER MAINTENANCE">UNDER MAINTENANCE</option>
            </select>
          </div>

          <button
            className="btn primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>

        <div className="settings-card green">
          <h3 className="card-title">Force Logout</h3>

          <div className="form-group">
            <label>User UID</label>
            <input
              type="text"
              value={forceUid}
              onChange={(e) => setForceUid(e.target.value)}
              placeholder="Enter user UID"
            />
          </div>

          <button className="btn primary" onClick={handleForceLogoutUser}>
            Force Logout User
          </button>

          <button className="btn primary" onClick={handleForceLogoutAll}>
            Force Logout ALL Users
          </button>

          {forceMsg && <div className="alert success">{forceMsg}</div>}
          {forceError && <div className="alert error">{forceError}</div>}
        </div>
      </div>
    </div>
  );
}

